import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import { ProgrammeIcon } from '../components/programmes/ProgrammeUI';
import { getProgramme } from '../data/programmeJourneys';
import {
  PROGRAMME_SYSTEM_MESSAGES,
  getApplicationStatusContent,
} from '../data/programmeMessages';
import {
  getApplicationDraft,
  saveApplicationDraft,
  submitProgrammeApplication,
} from '../services/programmeJourneyService';
import '../styles/pages/ProgrammeJourneys.css';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatSubmissionDate = (submittedAt) => {
  if (!submittedAt) return '';

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(submittedAt));
};

const ProgrammeApplicationPage = ({ kind }) => {
  const programme = getProgramme(kind);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(
    () => getApplicationDraft(programme.key)?.data || { consent: false }
  );
  const [errors, setErrors] = useState({});
  const [reviewing, setReviewing] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorSummaryRef = useRef(null);

  const step = programme.applicationSteps[currentStep];
  const isLastFormStep =
    currentStep === programme.applicationSteps.length - 1;
  const journeyStepCount = programme.applicationSteps.length + 1;
  const activeJourneyStep = reviewing
    ? journeyStepCount
    : currentStep + 1;
  const progress = useMemo(
    () => (activeJourneyStep / journeyStepCount) * 100,
    [activeJourneyStep, journeyStepCount]
  );

  useEffect(() => {
    if (!submission) saveApplicationDraft(programme.key, formData);
  }, [formData, programme.key, submission]);

  const updateField = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  const validateStep = () => {
    const nextErrors = {};

    step.fields.forEach((field) => {
      const value = formData[field.name];
      if (field.required && !String(value || '').trim()) {
        nextErrors[field.name] =
          field.validationMessage || `Complete ${field.label.toLowerCase()}.`;
      } else if (
        field.type === 'email' &&
        value &&
        !emailPattern.test(String(value))
      ) {
        nextErrors[field.name] = 'Enter a valid email address.';
      }
    });

    if (isLastFormStep && !formData.consent) {
      nextErrors.consent =
        'Confirm that the information is accurate and that you are authorised to submit it.';
    }

    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      window.requestAnimationFrame(() => {
        document.getElementById(firstError)?.focus();
      });
    }
    return Object.keys(nextErrors).length === 0;
  };

  const handleStepSubmit = (event) => {
    event.preventDefault();
    if (!validateStep()) return;

    if (!isLastFormStep) {
      setCurrentStep((current) => current + 1);
      return;
    }

    setReviewing(true);
  };

  const submitApplication = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const nextSubmission = await submitProgrammeApplication(
        programme.key,
        formData
      );
      setSubmission(nextSubmission);
    } catch (error) {
      setSubmitError(PROGRAMME_SYSTEM_MESSAGES.applicationFailure);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submission) {
    const reference = submission.reference || submission.id;
    const statusContent = getApplicationStatusContent(submission.status);
    const submittedOn = formatSubmissionDate(submission.submittedAt);

    return (
      <PageWrapper>
        <SEO
          description={`${programme.title} application confirmation.`}
          title={`Application complete | ${programme.title}`}
        />
        <main
          className={`pj-flow-page pj-theme--${programme.accent}`}
          data-programme={programme.key}
        >
          <div className="pj-flow-shell">
            <section className="pj-completion" aria-labelledby="completion-title">
              <span className="pj-completion__icon" aria-hidden="true">
                <ProgrammeIcon name="check" size={34} />
              </span>
              <h1 id="completion-title">Application submitted</h1>
              <p>
                Your application was submitted
                {submittedOn ? ` on ${submittedOn}` : ''}. Keep reference{' '}
                <strong>{reference}</strong> for your records. You can use the
                participant portal to track its status and receive updates.
              </p>
              <dl className="pj-submission-summary">
                <div>
                  <dt>Application reference</dt>
                  <dd>{reference}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{statusContent.label}</dd>
                </div>
              </dl>
              <div className="pj-completion__actions">
                <Link
                  className="pj-action pj-action--primary"
                  to={`/${programme.slug}/access`}
                >
                  Set up portal access
                  <ProgrammeIcon name="arrow" size={18} />
                </Link>
                <Link
                  className="pj-action pj-action--secondary"
                  to={`/${programme.slug}/login`}
                >
                  Sign in
                </Link>
              </div>
            </section>
          </div>
        </main>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO
        description={`Apply to ${programme.title}.`}
        title={`Apply | ${programme.title}`}
      />

      <main
        className={`pj-flow-page pj-theme--${programme.accent}`}
        data-programme={programme.key}
      >
        <div className="pj-flow-shell">
          <section className="pj-flow-hero" aria-labelledby="application-title">
            <h1 id="application-title">
              {programme.key === 'zone'
                ? 'Apply to the Zone'
                : 'Fellowship Application'}
            </h1>
            <p>{programme.applicationIntro}</p>
            <p className="pj-mobile-step">
              Step {activeJourneyStep} of {journeyStepCount} —{' '}
              {reviewing ? 'Review' : step.title}
            </p>

            <ol className="pj-form-steps" aria-label="Application progress">
              {programme.applicationSteps.map((item, index) => (
                <li
                  className={
                    !reviewing && index === currentStep
                      ? 'is-active'
                      : reviewing || index < currentStep
                        ? 'is-complete'
                        : ''
                  }
                  key={item.title}
                >
                  <span>{index + 1}</span>
                  {item.title}
                </li>
              ))}
              <li className={reviewing ? 'is-active' : ''}>
                <span>{journeyStepCount}</span>
                Review
              </li>
            </ol>
          </section>

          <section className="pj-before-begin" aria-labelledby="before-begin-title">
            <div>
              <h2 id="before-begin-title">Before you begin</h2>
              <p>{programme.applicationPreparation.summary}</p>
            </div>
            <ul>
              {programme.applicationPreparation.items.map((item) => (
                <li key={item}>
                  <ProgrammeIcon name="check" size={17} />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <div
            className={`pj-flow-layout ${reviewing ? 'pj-flow-layout--review' : ''}`}
          >
            <section
              className={`pj-form-panel ${reviewing ? 'pj-review-panel' : ''}`}
              aria-labelledby="application-step-title"
            >
              <div className="pj-progress" aria-hidden="true">
                <span style={{ width: `${progress}%` }} />
              </div>

              {reviewing ? (
                <>
                  <div className="pj-form-heading">
                    <h2 id="application-step-title">
                      Review your application
                    </h2>
                    <p>
                      Check each section before completing the application.
                    </p>
                  </div>

                  <div className="pj-review-sections">
                    {programme.applicationSteps.map((reviewStep, stepIndex) => (
                      <section key={reviewStep.title}>
                        <div className="pj-review-section__heading">
                          <h3>{reviewStep.title}</h3>
                          <button
                            onClick={() => {
                              setCurrentStep(stepIndex);
                              setReviewing(false);
                            }}
                            type="button"
                          >
                            Edit
                          </button>
                        </div>
                        <dl>
                          {reviewStep.fields.map((field) => (
                            <div key={field.name}>
                              <dt>{field.label}</dt>
                              <dd>{formData[field.name] || 'Not provided'}</dd>
                            </div>
                          ))}
                        </dl>
                      </section>
                    ))}
                  </div>

                  <section className="pj-review-acknowledgement">
                    <h3>Submission acknowledgement</h3>
                    <p>
                      You confirmed that the information in this application is
                      accurate to the best of your knowledge and that you are
                      authorised to submit it.
                    </p>
                    <p>
                      The Government of Barbados, through the Ministry of
                      Innovation, Industry, Science and Technology, will use the
                      information provided to assess and administer the
                      application.{' '}
                      <Link to="/privacy-policy">
                        Read the programme privacy notice.
                      </Link>
                    </p>
                  </section>

                  {submitError ? (
                    <p className="pj-login-error" role="alert">
                      {submitError}
                    </p>
                  ) : null}

                  <div className="pj-form-actions">
                    <button
                      className="pj-button pj-button--secondary"
                      disabled={isSubmitting}
                      onClick={() => setReviewing(false)}
                      type="button"
                    >
                      Back
                    </button>
                    <button
                      className="pj-button pj-button--primary"
                      disabled={isSubmitting}
                      onClick={submitApplication}
                      type="button"
                    >
                      {isSubmitting ? 'Submitting application…' : 'Submit application'}
                      {!isSubmitting ? (
                        <ProgrammeIcon name="arrow" size={18} />
                      ) : null}
                    </button>
                  </div>
                </>
              ) : (
                <form noValidate onSubmit={handleStepSubmit}>
                  <div className="pj-form-heading">
                    <h2 id="application-step-title">{step.title}</h2>
                    <p>Required fields are marked with an asterisk (*).</p>
                  </div>

                  {Object.keys(errors).length > 0 ? (
                    <p
                      className="pj-error-summary"
                      ref={errorSummaryRef}
                      role="alert"
                    >
                      Check {Object.keys(errors).length}{' '}
                      {Object.keys(errors).length === 1 ? 'field' : 'fields'}{' '}
                      before continuing.
                    </p>
                  ) : null}

                  <div className="pj-field-grid">
                    {step.fields.map((field) => {
                      const errorId = `${field.name}-error`;
                      const commonProps = {
                        'aria-describedby': errors[field.name]
                          ? errorId
                          : undefined,
                        'aria-invalid': Boolean(errors[field.name]),
                        id: field.name,
                        name: field.name,
                        onChange: (event) =>
                          updateField(field.name, event.target.value),
                        value: formData[field.name] || '',
                      };

                      return (
                        <div
                          className={`pj-field ${field.wide ? 'pj-field--wide' : ''}`}
                          key={field.name}
                        >
                          <label htmlFor={field.name}>
                            {field.label}
                            {field.required ? (
                              <span aria-hidden="true"> *</span>
                            ) : null}
                          </label>

                          {field.type === 'textarea' ? (
                            <textarea
                              {...commonProps}
                              placeholder={field.placeholder}
                              rows="5"
                            />
                          ) : field.type === 'select' ? (
                            <select {...commonProps}>
                              <option value="">Select an option</option>
                              {field.options.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              {...commonProps}
                              autoComplete={field.autoComplete}
                              placeholder={field.placeholder}
                              type={field.type}
                            />
                          )}

                          {errors[field.name] ? (
                            <span
                              className="pj-field-error"
                              id={errorId}
                            >
                              {errors[field.name]}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  {isLastFormStep ? (
                    <div className="pj-consent">
                      <label>
                        <input
                          checked={Boolean(formData.consent)}
                          onChange={(event) =>
                            updateField('consent', event.target.checked)
                          }
                          type="checkbox"
                        />
                        <span>
                          I confirm that the information in this application is
                          accurate to the best of my knowledge and that I am
                          authorised to submit it.
                        </span>
                      </label>
                      <p className="pj-privacy-notice">
                        The Government of Barbados, through the Ministry of
                        Innovation, Industry, Science and Technology, will use
                        the information provided to assess and administer the
                        application.{' '}
                        <Link to="/privacy-policy">
                          Read the programme privacy notice.
                        </Link>
                      </p>
                      {errors.consent ? (
                        <span className="pj-field-error">
                          {errors.consent}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="pj-form-actions">
                    <button
                      className="pj-button pj-button--secondary"
                      disabled={currentStep === 0}
                      onClick={() =>
                        setCurrentStep((current) => Math.max(0, current - 1))
                      }
                      type="button"
                    >
                      Back
                    </button>
                    <button
                      className="pj-button pj-button--primary"
                      type="submit"
                    >
                      {isLastFormStep ? 'Review application' : 'Continue'}
                      <ProgrammeIcon name="arrow" size={18} />
                    </button>
                  </div>
                </form>
              )}
            </section>

            {!reviewing ? (
              <aside className="pj-flow-intro">
                <p className="pj-flow-index">
                  Step {currentStep + 1} of {journeyStepCount}
                </p>
                <h2>
                  {programme.key === 'zone'
                    ? 'Prepare your experiment.'
                    : 'Tell us about your practice.'}
                </h2>
                <p>
                  {programme.key === 'zone'
                    ? 'Provide the clearest information currently available. You can refine experiment details during review.'
                    : 'Share enough context for the programme team to understand your work, perspective and participation needs.'}
                </p>
              </aside>
            ) : null}
          </div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default ProgrammeApplicationPage;
